import type { ReactNode } from 'react';

export type StepperStatus = 'done' | 'active' | 'pending' | 'error';

export interface StepperItem {
  /** Identificador estable: sirve de key y vuelve en `onSelect`. */
  id: string;
  label: string;
  /** Línea secundaria bajo la etiqueta. */
  description?: string;
  /** Reemplaza el marcador por defecto. */
  icon?: ReactNode;
  status: StepperStatus;
  /** No se puede clickear aunque haya `onSelect`. */
  disabled?: boolean;
}

export interface StepperProps {
  items: StepperItem[];
  ariaLabel?: string;
  /** Sin esto el stepper es de sólo lectura. */
  onSelect?: (id: string) => void;
  className?: string;
}

/**
 * Marcador por defecto.
 *
 * Cada estado se distingue por **forma y símbolo**, no sólo por color: un
 * usuario con daltonismo no puede separar "completado" de "con error" si la
 * única diferencia es verde contra rojo. El número también sirve de ancla
 * verbal cuando alguien pide ayuda por teléfono.
 */
function marcador(status: StepperStatus, numero: number): ReactNode {
  if (status === 'done') return '✓';
  if (status === 'error') return '!';
  return numero;
}

const ESTILO_MARCADOR: Record<StepperStatus, string> = {
  done: 'bg-surface-brand-soft text-content-brand',
  active: 'bg-surface-brand-soft text-content-brand ring-2 ring-edge-focus',
  pending: 'bg-surface-sunken text-content-tertiary',
  error: 'bg-surface-sunken text-content-primary ring-2 ring-edge-strong',
};

/** Texto que anuncia el estado. Sin esto, el lector de pantalla sólo oye el número. */
const ESTADO_LEIDO: Record<StepperStatus, string> = {
  done: 'completado',
  active: 'en curso',
  pending: 'pendiente',
  error: 'con errores',
};

/**
 * Lista vertical de pasos.
 *
 * Va en columna y no en fila porque las etiquetas del wizard son frases —
 * "Configuración del ERP", "Base de conocimiento"— y en horizontal se cortan o
 * empujan el contenido.
 */
export function Stepper({ items, ariaLabel = 'Pasos', onSelect, className }: StepperProps) {
  return (
    <nav aria-label={ariaLabel} data-testid="stepper">
      <ol
        className={`flex flex-col gap-1 rounded-lg border border-edge-subtle bg-surface-base p-4 ${className ?? ''}`}
      >
        {items.map((item, i) => {
          // Un paso activo no navega a sí mismo, y uno deshabilitado no navega.
          const clickeable = Boolean(onSelect) && !item.disabled && item.status !== 'active';

          return (
            <li
              key={item.id}
              data-testid={`stepper-item-${item.id}`}
              data-status={item.status}
              aria-current={item.status === 'active' ? 'step' : undefined}
            >
              <button
                type="button"
                disabled={!clickeable}
                onClick={clickeable ? () => onSelect?.(item.id) : undefined}
                className={`flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                  clickeable ? 'hover:bg-surface-sunken' : ''
                } ${item.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${ESTILO_MARCADOR[item.status]}`}
                >
                  {item.icon ?? marcador(item.status, i + 1)}
                </span>

                <span className="min-w-0">
                  <span
                    className={`block truncate text-sm ${
                      item.status === 'active'
                        ? 'font-medium text-content-primary'
                        : 'text-content-secondary'
                    }`}
                  >
                    {item.label}
                  </span>
                  {item.description ? (
                    <span className="block truncate text-xs text-content-tertiary">
                      {item.description}
                    </span>
                  ) : null}
                  {/* El estado va en texto para quien no ve el color del marcador. */}
                  <span className="sr-only">{ESTADO_LEIDO[item.status]}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
